import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Autocomplete,
  Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, People as PeopleIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import api from '../services/api';
import UserManagementDialog from '../components/UserManagementDialog';

interface Company {
  id: string;
  name: string;
  companyNumber?: string | null;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  companyType: 'external' | 'internal' | 'system';
  isActive: boolean;
  defaultTasks?: string[];
  organization: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CompanyFormData {
  organizationId: string;
  name: string;
  companyNumber: string;
  description: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  companyType: 'external' | 'internal' | 'system';
  isActive: boolean;
  defaultTasks: string[];
}

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>({
    organizationId: '',
    name: '',
    companyNumber: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    companyType: 'external',
    isActive: true,
    defaultTasks: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterType, setFilterType] = useState<'all' | 'external' | 'internal' | 'system'>('all');
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  useEffect(() => {
    loadCompanies();
    loadOrganizations();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.getCompanies();
      setCompanies(response.data || []);
    } catch (err: any) {
      console.error('Error loading companies:', err);
      setError(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
    }
  };

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        organizationId: company.organization.id,
        name: company.name,
        companyNumber: company.companyNumber || '',
        description: company.description || '',
        contactEmail: company.contactEmail || '',
        contactPhone: company.contactPhone || '',
        address: company.address || '',
        companyType: company.companyType,
        isActive: company.isActive,
        defaultTasks: company.defaultTasks || []
      });
    } else {
      setEditingCompany(null);
      setFormData({
        organizationId: '',
        name: '',
        companyNumber: '',
        description: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        companyType: 'external',
        isActive: true,
        defaultTasks: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompany(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (editingCompany) {
        await api.updateCompany(editingCompany.id, formData);
      } else {
        // For new companies, don't send defaultTasks - let backend inherit from organization
        const { defaultTasks, ...createData } = formData;
        await api.createCompany(createData);
      }
      loadCompanies();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving company:', err);
      setError(err.response?.data?.message || 'Failed to save company');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await api.deleteCompany(companyId);
        loadCompanies();
      } catch (err: any) {
        console.error('Error deleting company:', err);
        setError(err.response?.data?.message || 'Failed to delete company');
      }
    }
  };

  const handleOpenUserManagement = (company: Company) => {
    setSelectedCompany(company);
    setUserManagementOpen(true);
  };

  const handleCloseUserManagement = () => {
    setUserManagementOpen(false);
    setSelectedCompany(null);
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case 'external': return 'primary';
      case 'internal': return 'secondary';
      case 'system': return 'default';
      default: return 'default';
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.companyNumber && company.companyNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (company.description && company.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && company.isActive) ||
      (filterStatus === 'inactive' && !company.isActive);

    const matchesType = filterType === 'all' || company.companyType === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'companyNumber', headerName: 'Company #', width: 130 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { 
      field: 'companyType', 
      headerName: 'Type', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={getCompanyTypeColor(params.value) as any}
          size="small"
        />
      )
    },
    { field: 'contactEmail', headerName: 'Email', width: 200 },
    { field: 'contactPhone', headerName: 'Phone', width: 150 },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { field: 'organization.name', headerName: 'Organization', width: 150 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PeopleIcon />}
          label="Manage Users"
          onClick={() => handleOpenUserManagement(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteCompany(params.row.id)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Companies Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Company
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search companies"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 200 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value as any)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            {filteredCompanies.length} of {companies.length} companies
          </Typography>
        </Box>
      </Paper>

      {/* Companies Table */}
      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={filteredCompanies}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Add/Edit Company Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                value={formData.organizationId}
                label="Organization"
                onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                disabled={!!editingCompany}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Company Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Company number"
              value={formData.companyNumber}
              onChange={(e) => setFormData({ ...formData, companyNumber: e.target.value })}
              helperText="Optional reference code for display or integrations (not unique)"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
            />

            <FormControl fullWidth>
              <InputLabel>Company Type</InputLabel>
              <Select
                value={formData.companyType}
                label="Company Type"
                onChange={(e) => setFormData({ ...formData, companyType: e.target.value as any })}
              >
                <MenuItem value="external">External Customer</MenuItem>
                <MenuItem value="internal">Internal Work</MenuItem>
                <MenuItem value="system">System Generated</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
            />

            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
            />

            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              multiline
              rows={2}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            {editingCompany && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Default Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These tasks will be automatically available for all new accounts created in this company.
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.defaultTasks}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, defaultTasks: newValue });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Default Tasks"
                      placeholder="Type a task name and press Enter"
                      helperText="Press Enter to add a new task"
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCompany ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* User Management Dialog */}
      {selectedCompany && (
        <UserManagementDialog
          open={userManagementOpen}
          onClose={handleCloseUserManagement}
          title={selectedCompany.name}
          entityId={selectedCompany.id}
          entityType="company"
          organizationId={selectedCompany.organization.id}
        />
      )}
    </Box>
  );
}
